import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/ui/UnifiedHeader';
import Icon from '../../components/AppIcon';
import FeaturedSection from './components/FeaturedSection';
import SearchBar from './components/SearchBar';
import FilterSidebar from './components/FilterSidebar';
import ResourceCard from './components/ResourceCard';
import LearningPaths from './components/LearningPaths';
// import WebinarLibrary from './components/WebinarLibrary'; // Webinars disabled - no content yet
import NewsletterSubscription from './components/NewsletterSubscription';
import { ENHANCED_INSIGHTS } from '../../constants/insightsData';
import { useAnchorScroll } from '../../hooks/useAnchorScroll';

import SharedFooter from '../../components/SharedFooter';

const ResourcesInsightsKnowledgeAuthority = () => {
  // Handle anchor scrolling
  useAnchorScroll();
  
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [learningProgress, setLearningProgress] = useState([]);
  const [totalProgress, setTotalProgress] = useState(0);
  let user = null;
  let userProfile = null;
  let loading = true;
  
  try {
    const authContext = useAuth();
    user = authContext.user;
    userProfile = authContext.userProfile;
    loading = authContext.loading;
  } catch (error) {
    console.error('Auth context error:', error);
    // Continue without auth
    loading = false;
  }
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('relevance');
  const [filters, setFilters] = useState({
    type: [],
    audience: [],
    topic: [],
    industry: [],
    difficulty: []
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');

  const [showOTPModal, setShowOTPModal] = useState(false);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  // Load learning progress for approved users
  const loadLearningProgress = async () => {
    if (!user || userProfile?.approval_status !== 'approved') return;
    
    try {
      const { data, error } = await supabase
        .from('user_learning_progress')
        .select(`
          *,
          learning_paths (
            title,
            modules_count
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'in_progress');

      if (error) throw error;
      
      setLearningProgress(data || []);
      
      // Calculate total progress
      if (data && data.length > 0) {
        const total = data.reduce((sum, progress) => sum + (progress.progress_percentage || 0), 0);
        setTotalProgress(Math.round(total / data.length));
      }
    } catch (error) {
      console.error('Error loading learning progress:', error);
    }
  };

  // Load progress when user is approved
  useEffect(() => {
    if (user && userProfile?.approval_status === 'approved') {
      loadLearningProgress();
    }
  }, [user, userProfile]);

  const resources = ENHANCED_INSIGHTS;

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
  };

  const handleFilterChange = (categoryId, value, checked) => {
    setFilters(prev => ({
      ...prev,
      [categoryId]: checked
        ? [...prev[categoryId], value]
        : prev[categoryId].filter(item => item !== value)
    }));
  };

  const toggleFilterSidebar = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  // Filter and sort resources based on current state
  const filteredResources = resources.filter(resource => {
    // Search filter
    if (searchTerm && !resource.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !resource.description.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Type filter
    if (filters.type.length > 0 && !filters.type.includes(resource.type)) {
      return false;
    }

    return true;
  });

  return (
    <>
      <Helmet>
        <title>Resources & Insights - Knowledge Authority | Bootminds Elite</title>
        <meta name="description" content="Access comprehensive GCC resources, expert insights, and industry intelligence. Strategic guides, market reports, and exclusive content for GCC establishment and operations." />
        <meta name="keywords" content="GCC resources, market intelligence, strategic guides, compliance checklists, talent acquisition, operational excellence, industry insights" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        {/* User Status Notification - Top Right Corner */}
        {user && userProfile && (
          <div className="fixed top-20 right-4 z-[9999]">
            <div 
              className="bg-card border border-border rounded-lg p-3 shadow-lg flex items-center space-x-3 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                <span className="text-primary-foreground text-sm font-medium">
                  {userProfile?.full_name?.charAt(0)?.toLowerCase() || user.email?.charAt(0)?.toLowerCase() || 'h'}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-text-primary">
                  {userProfile?.full_name || user.email?.split('@')[0] || 'hitesh.m'}
                </span>
                <span className="text-xs text-text-secondary">
                  {userProfile?.approval_status === 'approved' ? 'Approved' : 'Pending'}
                </span>
              </div>
              <Icon name="ChevronRight" size={16} className="text-text-secondary" />
            </div>
            
            {/* Dropdown Menu */}
            {showUserMenu && (
              <div className="absolute top-full right-0 mt-2 w-80 bg-card border border-border rounded-lg shadow-lg">
                <div className="p-4 border-b border-border">
                  <h3 className="font-medium text-text-primary">User Account</h3>
                  <p className="text-sm text-text-secondary mt-1">
                    Status: {userProfile?.approval_status === 'approved' ? 'Approved' : 'Pending'}
                  </p>
                </div>
                
                {/* Progress Section */}
                {userProfile?.approval_status === 'approved' && learningProgress.length > 0 && (
                  <div className="p-4 border-b border-border">
                    <div className="flex items-center space-x-2 mb-3">
                      <Icon name="BookOpen" size={16} className="text-primary" />
                      <h4 className="font-medium text-text-primary">Learning Progress</h4>
                    </div>
                    
                    {/* Overall Progress */}
                    <div className="mb-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-text-secondary">Overall Progress</span>
                        <span className="text-primary font-medium">{totalProgress}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-primary h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${totalProgress}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    {/* Individual Learning Paths */}
                    <div className="space-y-2">
                      {learningProgress.slice(0, 3).map((progress) => (
                        <div key={progress.id} className="flex items-center justify-between text-xs">
                          <span className="text-text-secondary truncate flex-1 mr-2">
                            {progress.learning_paths?.title || 'Learning Path'}
                          </span>
                          <span className="text-primary font-medium">
                            {progress.progress_percentage || 0}%
                          </span>
                        </div>
                      ))}
                      {learningProgress.length > 3 && (
                        <p className="text-xs text-text-secondary text-center">
                          +{learningProgress.length - 3} more paths
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="p-2">
                  <button className="w-full flex items-center space-x-2 p-2 text-left text-sm text-text-secondary hover:text-error hover:bg-error/5 rounded-lg transition-colors">
                    <Icon name="LogOut" size={16} />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        
        <main className="pt-20">
          {/* Hero Section */}
          <section className="py-16 lg:py-20 bg-gradient-to-b from-background to-background-alt content-animate">
            <div className="container-executive">
              <div className="max-w-4xl mx-auto text-center space-y-8">
                <div className="inline-flex items-center px-4 py-2 bg-accent/10 text-accent rounded-full text-sm font-medium hero-title-animate">
                  <Icon name="BookOpen" size={16} className="mr-2" />
                  Knowledge Authority Center
                </div>
                <h1 className="font-headline font-bold text-4xl lg:text-5xl text-text-primary hero-title-animate">
                  Resources & Insights Hub
                </h1>
                <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed hero-description-animate">
                  Access our comprehensive library of strategic guides, market intelligence, and expert insights. 
                  Everything you need to establish and optimize your Global Capability Center operations.
                </p>
                
                {/* User Welcome Message */}
                {user && (
                  <div className="p-4 bg-primary/10 rounded-lg border border-primary/20 max-w-2xl mx-auto">
                    <div className="flex items-center justify-center">
                      <Icon name="User" size={20} className="text-primary mr-2" />
                      <p className="text-text-primary font-medium">
                        Welcome back! You have access to premium content.
                      </p>
                    </div>
                  </div>
                )}
                


                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6 pt-8">
                  {[
                    { icon: "FileText", label: "Resources", value: "50+" },
                    { icon: "BookOpen", label: "Guides & Reports", value: "25+" },
                    { icon: "Users", label: "Industry Experts", value: "10+" }
                  ].map((stat, index) => (
                    <div key={index} className="text-center stats-animate">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <Icon name={stat.icon} size={24} className="text-primary" />
                      </div>
                      <div className="font-headline font-bold text-2xl text-text-primary">
                        {stat.value}
                      </div>
                      <div className="text-text-secondary text-sm">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* SkillStack Staffing Overview */}
          <section id="staffing-models" className="py-16 bg-background">
            <div className="container-executive">
              <div className="max-w-5xl mx-auto text-center mb-12">
                <div className="inline-flex items-center space-x-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-text-primary">
                  <Icon name="Layers" size={16} />
                  <span>SkillStack™ Staffing</span>
                </div>
                <h2 className="mt-5 text-3xl lg:text-4xl font-headline font-bold text-text-primary">
                  Staffing that Launches Teams in Under 25 Days
                </h2>
                <p className="mt-4 text-lg text-text-secondary">
                  Contract, contract-to-hire, and full-time hiring models powered by SkillStack™ verification, AI-backed analytics, and a candidate-first experience. Choose the engagement that meets your velocity and conversion goals.
                </p>
              </div>

              <div className="overflow-x-auto rounded-3xl border border-border bg-card/80 shadow-xl">
                <table className="min-w-full divide-y divide-border text-left">
                  <thead className="bg-muted/40 text-sm uppercase tracking-wide text-text-secondary">
                    <tr>
                      <th className="px-6 py-4 font-semibold text-text-primary">Feature</th>
                      <th className="px-6 py-4 font-semibold text-text-primary">Contract Staffing</th>
                      <th className="px-6 py-4 font-semibold text-text-primary">Contract-to-Hire</th>
                      <th className="px-6 py-4 font-semibold text-text-primary">Full-Time (FTE)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border text-sm">
                    {[
                      {
                        label: 'Purpose',
                        contract: 'Flexible workforce for short- or mid-term needs',
                        c2h: 'Trial-to-hire model for mutual fit',
                        fte: 'Permanent talent for long-term roles'
                      },
                      {
                        label: 'Hiring Speed',
                        contract: '🚀 <25 days from request to onboarding',
                        c2h: '🚀 <25 days including evaluation',
                        fte: '⚡ <25 days from intake'
                      },
                      {
                        label: 'Skill Verification',
                        contract: 'SkillStack™ + human validation',
                        c2h: 'SkillStack™ + human validation',
                        fte: 'SkillStack™ + human validation'
                      },
                      {
                        label: 'Payroll & Compliance',
                        contract: 'Managed by Bootminds',
                        c2h: 'Managed by Bootminds until conversion',
                        fte: 'Managed by client post-joining'
                      },
                      {
                        label: 'Candidate Experience',
                        contract: 'Gift voucher, pickup & drop, engagement concierge',
                        c2h: 'Gift voucher, pickup & drop, performance dashboard',
                        fte: 'Gift voucher, pickup & drop, personalised onboarding'
                      },
                      {
                        label: 'Risk Cover',
                        contract: 'Free replacement within 15 days',
                        c2h: 'Replacement during contract window',
                        fte: '90-day replacement guarantee'
                      },
                      {
                        label: 'Cost Flexibility',
                        contract: 'Monthly billing, pay-per-use',
                        c2h: 'Flexible billing, pause or extend',
                        fte: 'One-time placement fee'
                      },
                      {
                        label: 'Ideal For',
                        contract: 'Short-term projects, rapid scale',
                        c2h: 'Mid-level & niche roles requiring evaluation',
                        fte: 'Strategic hires, leadership, long-term builds'
                      }
                    ].map((row) => (
                      <tr key={row.label} className="bg-background">
                        <th scope="row" className="whitespace-pre-wrap px-6 py-5 font-semibold text-text-primary align-top">{row.label}</th>
                        <td className="whitespace-pre-wrap px-6 py-5 text-text-secondary align-top">{row.contract}</td>
                        <td className="whitespace-pre-wrap px-6 py-5 text-text-secondary align-top">{row.c2h}</td>
                        <td className="whitespace-pre-wrap px-6 py-5 text-text-secondary align-top">{row.fte}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* CTA to dedicated staffing page */}
              <div className="mt-8 text-center">
                <a 
                  href="/services/staffing" 
                  className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-300 font-medium"
                >
                  <Icon name="ExternalLink" size={18} className="mr-2" />
                  View Full SkillStack™ Staffing Details
                </a>
              </div>
            </div>
          </section>

          {/* Candidate Experience */}
          <section id="candidate-experience" className="py-16 bg-background-alt">
            <div className="container-executive">
              <div className="grid gap-10 lg:grid-cols-2 items-center">
                <div className="order-last lg:order-first">
                  <div className="rounded-3xl border border-accent/20 bg-accent/10 p-8 shadow-lg">
                    <h3 className="text-2xl font-headline font-semibold text-accent mb-4">Candidate Experience Engine</h3>
                    <p className="text-text-secondary mb-6">
                      Every candidate is treated like a brand ambassador—because their experience dictates your market reputation. We blend human touch with analytics to ensure every interaction is memorable.
                    </p>
                    <ul className="space-y-3 text-text-secondary">
                      {[
                        'Amazon gift vouchers for completing SkillStack™ assessments — our thank-you for investing their time.',
                        'Door-to-door pickup and drop for onsite interviews to eliminate friction and anxiety.',
                        'Experience dashboards that share interview feedback, readiness scores, and cultural fit insights.',
                        'Candidate success managers who stay connected until onboarding is complete.'
                      ].map((item, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <Icon name="Sparkles" size={18} className="text-accent mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div>
                  <div className="inline-flex items-center space-x-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-text-primary mb-4">
                    <Icon name="HeartPulse" size={16} />
                    <span>People-first Differentiation</span>
                  </div>
                  <h2 className="text-3xl font-headline font-bold text-text-primary mb-4">
                    Engagement That Converts Offers into Enthusiasm
                  </h2>
                  <p className="text-lg text-text-secondary mb-6">
                    Our staffing engine is engineered for trust. Every touchpoint is scripted to elevate your employer brand and shorten decision cycles—so candidates say "yes" faster and stay longer.
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {[
                      {
                        label: 'SkillStack™ Signals',
                        detail: 'AI-led assessments blended with human interviewers to de-risk quality.'
                      },
                      {
                        label: 'Experience Analytics',
                        detail: 'NPS tracking, drop-off insights, and conversion dashboards delivered weekly.'
                      },
                      {
                        label: 'Brand Storytelling',
                        detail: 'Custom microsites, interview kits, and EVP playbooks tuned to your culture.'
                      },
                      {
                        label: 'Offer-to-Join Programs',
                        detail: 'Engagement pods that minimise attrition during notice period.'
                      }
                    ].map((item) => (
                      <div key={item.label} className="rounded-xl border border-border p-4 bg-card/80">
                        <p className="text-sm font-semibold text-text-primary">{item.label}</p>
                        <p className="text-sm text-text-secondary mt-1">{item.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Retention & Guarantees */}
          <section id="retention" className="py-16 bg-background">
            <div className="container-executive">
              <div className="rounded-3xl border border-success/20 bg-success/10 p-10 shadow-xl">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
                  <div className="max-w-3xl">
                    <div className="inline-flex items-center space-x-2 rounded-full bg-success/20 px-4 py-2 text-sm font-medium text-success mb-4">
                      <Icon name="ShieldCheck" size={16} />
                      <span>Retention & Risk Mitigation</span>
                    </div>
                    <h2 className="text-3xl font-headline font-bold text-text-primary mb-4">
                      Guarantees that De-risk Your Staffing Scale
                    </h2>
                    <p className="text-lg text-text-secondary mb-6">
                      From replacements to compliance, every staffing engagement is governed by measurable obligations so you can scale with confidence.
                    </p>
                    <ul className="space-y-3 text-text-secondary">
                      {[
                        'Free replacements within 15 days for contract roles and during the contract window for C2H.',
                        '90-day replacement guarantee for full-time hires with conversion analytics to spot early risks.',
                        'Performance dashboards covering productivity, attendance, and engagement across the cohort.',
                        'Full compliance handling—payroll, taxation, labour law, and background verification under one roof.'
                      ].map((point, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <Icon name="CheckCircle" size={18} className="text-success mt-0.5" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="lg:w-80 flex flex-col space-y-4">
                    <div className="rounded-2xl border border-border bg-card p-5 shadow">
                      <p className="text-sm font-semibold text-text-primary">25-Day Promise</p>
                      <p className="text-sm text-text-secondary mt-1">Every role filled within 25 days—or your next shortlist is free.</p>
                    </div>
                    <div className="rounded-2xl border border-border bg-card p-5 shadow">
                      <p className="text-sm font-semibold text-text-primary">True Partnership</p>
                      <p className="text-sm text-text-secondary mt-1">We stay through onboarding and transition to GCC teams when you are ready.</p>
                    </div>
                    <div className="rounded-2xl border border-border bg-card p-5 shadow">
                      <p className="text-sm font-semibold text-text-primary">Data Intelligence</p>
                      <p className="text-sm text-text-secondary mt-1">Access SkillStack™ dashboards to track conversions, retention, and talent quality trends.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* TA Strategy Report */}
          <section id="ta-strategy" className="py-16 bg-background-alt">
            <div className="container-executive">
              <div className="rounded-3xl border border-border bg-card/70 p-10 shadow-xl">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
                  <div className="max-w-3xl">
                    <div className="inline-flex items-center space-x-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-text-primary mb-4">
                      <Icon name="ClipboardList" size={16} />
                      <span>TA Strategy Report</span>
                    </div>
                    <h2 className="text-3xl font-headline font-bold text-text-primary mb-4">
                      $10,000 Workshop-Driven TA Blueprint
                    </h2>
                    <p className="text-lg text-text-secondary mb-6">
                      Stakeholder-driven blueprint covering 20+ sections—from campus programs to AI-driven workforce planning.
                    </p>
                    <ul className="space-y-3 text-text-secondary">
                      {[
                        'Campus placement roadmap and emerging university partnerships.',
                        'Parallel hiring recommendations and talent funnel diagnostics.',
                        'Contract vs. full-time staffing ratios optimized by function.',
                        'Internal mobility and promotion frameworks.',
                        'Market research, salary trends, and AI impact modelling.'
                      ].map((topic, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <Icon name="CheckCircle" size={18} className="text-primary mt-0.5" />
                          <span>{topic}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="lg:w-80 flex flex-col space-y-4">
                    <div className="rounded-2xl border border-border bg-card p-5 shadow">
                      <p className="text-sm font-semibold text-text-primary">Fixed Price</p>
                      <p className="text-sm text-text-secondary mt-1">$10,000 one-time fee for comprehensive TA strategy.</p>
                    </div>
                    <div className="rounded-2xl border border-border bg-card p-5 shadow">
                      <p className="text-sm font-semibold text-text-primary">2-Week Delivery</p>
                      <p className="text-sm text-text-secondary mt-1">Workshop-based approach with stakeholder interviews.</p>
                    </div>
                    <div className="rounded-2xl border border-border bg-card p-5 shadow">
                      <p className="text-sm font-semibold text-text-primary">20+ Sections</p>
                      <p className="text-sm text-text-secondary mt-1">Complete talent acquisition blueprint with actionable insights.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Workshop Methodology */}
          <section id="workshop" className="py-16 bg-background">
            <div className="container-executive">
              <div className="rounded-3xl border border-border bg-card/70 p-10 shadow-xl">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
                  <div className="max-w-3xl">
                    <div className="inline-flex items-center space-x-2 rounded-full bg-accent/10 px-4 py-2 text-sm font-medium text-accent mb-4">
                      <Icon name="Users2" size={16} />
                      <span>Workshop Methodology</span>
                    </div>
                    <h2 className="text-3xl font-headline font-bold text-text-primary mb-4">
                      Stakeholder Workshops that Build Authority and Alignment
                    </h2>
                    <p className="text-lg text-text-secondary mb-6">
                      We facilitate cross-functional workshops with HR, business, and CXO teams to co-create the hiring narrative.
                    </p>
                    <div className="grid gap-4 sm:grid-cols-2">
                      {[
                        {
                          label: 'Discovery Interviews',
                          description: 'Leadership, HR, and hiring manager interviews to surface expectations and risks.'
                        },
                        {
                          label: 'AI Impact Labs',
                          description: 'Scenario planning for automation, GenAI adoption, and workforce re-skilling.'
                        },
                        {
                          label: 'Experience Mapping',
                          description: 'Candidate and hiring manager journey mapping with differentiating touchpoints.'
                        },
                        {
                          label: 'Executive Playback',
                          description: 'Strategy validation with CXO team and implementation roadmap finalization.'
                        }
                      ].map((workshop, index) => (
                        <div key={index} className="bg-background border border-border rounded-xl p-4">
                          <h4 className="font-semibold text-text-primary mb-2">{workshop.label}</h4>
                          <p className="text-sm text-text-secondary">{workshop.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="lg:w-80 flex flex-col space-y-4">
                    <div className="rounded-2xl border border-border bg-card p-5 shadow">
                      <p className="text-sm font-semibold text-text-primary">Cross-Functional</p>
                      <p className="text-sm text-text-secondary mt-1">HR, business, and CXO teams collaborate in workshops.</p>
                    </div>
                    <div className="rounded-2xl border border-border bg-card p-5 shadow">
                      <p className="text-sm font-semibold text-text-primary">SkillStack™ Integration</p>
                      <p className="text-sm text-text-secondary mt-1">Workshop insights feed into talent intelligence platform.</p>
                    </div>
                    <div className="rounded-2xl border border-border bg-card p-5 shadow">
                      <p className="text-sm font-semibold text-text-primary">Actionable Output</p>
                      <p className="text-sm text-text-secondary mt-1">Co-created hiring narrative with implementation roadmap.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Featured Content */}
          <section className="py-16">
            <div className="container-executive">
              <FeaturedSection />
            </div>
          </section>

          {/* Search and Filter Section */}
          <section className="py-16 bg-muted/30">
            <div className="container-executive">
              <SearchBar
                onSearch={handleSearch}
                onSortChange={handleSortChange}
                sortBy={sortBy}
              />

              <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Filter Sidebar */}
                <div className="lg:col-span-1">
                  <FilterSidebar
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    isOpen={isFilterOpen}
                    onToggle={toggleFilterSidebar}
                  />
                </div>

                {/* Resources Grid */}
                <div className="lg:col-span-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {filteredResources.map((resource) => (
                      <ResourceCard
                        key={resource.id}
                        resource={resource}
                        featured={resource.featured}
                      />
                    ))}
                  </div>

                  {/* Load More */}
                  <div className="text-center mt-12">
                    <button className="inline-flex items-center px-6 py-3 border border-border rounded-lg hover:bg-muted transition-colors duration-300 font-medium">
                      <Icon name="MoreHorizontal" size={18} className="mr-2" />
                      Load More Resources
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Learning Paths */}
          <section className="py-16">
            <div className="container-executive">
              <LearningPaths />
            </div>
          </section>

          {/* Newsletter Subscription */}
          <section className="py-16">
            <div className="container-executive">
              <NewsletterSubscription />
            </div>
          </section>

          {/* Additional Resources CTA */}
          <section className="py-16 bg-muted/30">
            <div className="container-executive">
              <div className="text-center">
                <Icon name="Lightbulb" size={48} className="text-accent mx-auto mb-6" />
                <h2 className="font-headline font-bold text-3xl text-text-primary mb-4">
                  Need Strategic Market Intelligence?
                </h2>
                <p className="text-lg text-text-secondary mb-8 max-w-2xl mx-auto">
                  Explore our comprehensive engagement models intelligence and real-time market insights 
                  in our dedicated Market Intelligence Hub.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/market-intelligence"
                    className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-300 font-medium"
                  >
                    <Icon name="BarChart3" size={18} className="mr-2" />
                    Explore Market Intelligence Hub
                  </a>
                  <a
                    href="/contact"
                    className="inline-flex items-center px-6 py-3 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-colors duration-300 font-medium"
                  >
                    <Icon name="Calendar" size={18} className="mr-2" />
                    Schedule Expert Consultation
                  </a>
                  <a
                    href="/about"
                    className="inline-flex items-center px-6 py-3 border border-border rounded-lg hover:bg-muted transition-colors duration-300 font-medium"
                  >
                    <Icon name="Users" size={18} className="mr-2" />
                    Meet Our Team
                  </a>
                </div>
              </div>
            </div>
          </section>
        </main>

        <SharedFooter />
      </div>
      

    </>
  );
};

export default ResourcesInsightsKnowledgeAuthority;